import { DocxTemplate } from '@range.io/basic-types'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, FlexColumn, FlexRow, Icon, Text } from '../../components-reusable/index.js'
import { listDocxTemplates } from '../../firebase/firebase-facade.js'
import { styled } from '../../range-theme/index.js'
import { ReduxSelectors } from '../../redux/index.js'

const StyledOverlay = styled(FlexColumn, {
    width: '100vw',
    height: '100vh',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: '$neutralOverlay',
    ai: 'center',
    justifyContent: 'center',
    position: 'fixed',
    zIndex: 999,
})

const StyledContent = styled(FlexColumn, {
    bg: '$neutral10Alpha',
    backdropFilter: 'blur(12px) saturate(190%) contrast(50%) brightness(130%)',
    color: '$neutral04',
    width: '800px',
    p: '32px',
    border: '1px solid $neutral07',
    br: '6px',
    boxShadow: '0px 40px 40px #00000025',
    zIndex: 1000,
    gap: '32px',
})

const StyledTemplateSection = styled(FlexColumn, {
    background: '$neutral09',
    br: '6px',
    border: '1px solid $neutral07',
    overflow: 'hidden',
})

const StyledTemplateRow = styled(FlexRow, {
    gap: '12px',
    padding: '0px 8px 0px 16px',
    height: '56px',
    alignItems: 'center',
    background: '$neutral09',
    borderBottom: '1px solid $neutral07',
    color: '$neutral04',
    cursor: 'pointer',
    transitionDuration: '0.2s',

    '&:last-child': {
        borderBottom: 'none',
    },

    '&:hover': {
        background: '$neutral08',
        '& .preview-icon': {
            opacity: 1,
        },
    },

    variants: {
        selected: {
            true: {
                background: '$neutral08',
            },
        },
    },
})

const StyledSectionHeader = styled(FlexRow, {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 8px 0px 16px',
    height: '56px',
    borderBottom: '1px solid $neutral07',
    fontWeight: '400',
    '& > *': {
        color: '$neutral05',
    },
    '&:last-child': {
        borderBottom: 'none',
    },
})

const StyledButtonContainer = styled(FlexRow, {
    mt: 'auto',
    gap: '12px',
    justifyContent: 'flex-end',
})

const StyledManageButton = styled(Button, {
    gap: '8px',
})

/**
 * Template section component for both default and custom templates
 * @sig ReportSection :: {String, [String], String, String -> (), Boolean} -> ReactElement
 */
const ReportSection = ({
    title,
    templates,
    selectedTemplate,
    onTemplateSelect,
    showManageButton,
    isCustomSection,
    toReports,
}) => {
    const templateRow = template => (
        <StyledTemplateRow
            key={template}
            selected={template === selectedTemplate}
            onClick={() => onTemplateSelect(template, isCustomSection)}
        >
            <Icon
                name={template === selectedTemplate ? 'badge' : 'indicator'}
                iconSize="16px"
                css={{ color: template === selectedTemplate ? '$primary04' : '$neutral07' }}
            />
            <FlexColumn css={{ flex: 1 }}>
                <Text css={{ color: '$neutral04' }}>{template}</Text>
            </FlexColumn>
            {/* <StyledPreviewIcon name="eye" iconSize="16px" className="preview-icon" /> */}
        </StyledTemplateRow>
    )

    const templateCount = templates.length
    const titleWithCount = `${title} (${templateCount})`

    return (
        <StyledTemplateSection>
            <StyledSectionHeader>
                <Text css={{ fs: '16px', color: '$neutral05' }}>{titleWithCount}</Text>
                {showManageButton && (
                    <StyledManageButton variant="secondary" size="lg" onClick={toReports}>
                        <Icon name="edit" iconSize="18px" />
                        <Text>Manage Templates</Text>
                    </StyledManageButton>
                )}
            </StyledSectionHeader>
            {templates.map(templateRow)}
        </StyledTemplateSection>
    )
}

/**
 * Empty state for custom templates section
 * @sig EmptyCustomTemplatesSection :: () -> ReactElement
 */
const EmptyCustomTemplatesSection = ({ toReports }) => (
    <StyledTemplateSection>
        <StyledSectionHeader>
            <Text css={{ fs: '16px', color: '$neutral05' }}>Custom Report Templates (0)</Text>
            <StyledManageButton variant="secondary" size="lg" onClick={toReports}>
                <Icon name="edit" iconSize="18px" />
                <Text>Manage Templates</Text>
            </StyledManageButton>
        </StyledSectionHeader>
        <FlexColumn css={{ alignItems: 'center', py: '32px', gap: '0px' }}>
            <Text css={{ color: '$neutral05', textAlign: 'center', fs: 16 }}>0 custom report templates</Text>
            <Text css={{ color: '$neutral05', textAlign: 'center', fs: 14 }}>
                Get started by clicking the Manage Templates button to learn more
            </Text>
        </FlexColumn>
    </StyledTemplateSection>
)

/**
 * Modal for selecting a report template
 * @sig SelectReportTemplateModal :: {[String], [String], (String, Boolean) -> (), () -> ()} -> ReactElement
 *
 * _listTemplates allows tests to pass in values easily
 */
const SelectReportTemplateModal = ({ onTemplateSelect, onCancel, _listTemplates = listDocxTemplates, toReports }) => {
    const handleTemplateSelect = (template, isCustom) => {
        setSelectedTemplate(template)
        setIsCustomTemplate(isCustom)
    }

    const handleCreateReport = () => onTemplateSelect(selectedTemplate, isCustomTemplate)

    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [isCustomTemplate, setIsCustomTemplate] = useState(false)
    const [docxTemplates, setDocxTemplates] = useState([])
    const organization = useSelector(ReduxSelectors.selectedOrganization)
    const defaultReportTemplates = DocxTemplate.defaultTemplateNames(docxTemplates)
    const customReportTemplates = DocxTemplate.customTemplateNames(docxTemplates)

    // fetch the templates after mounting the component
    useEffect(() => {
        const fetchTemplates = async () => {
            const docxTemplates = await _listTemplates(organization.id)
            setDocxTemplates(docxTemplates)
        }
        fetchTemplates()
    }, [])

    const defaultReportsSection = (
        <ReportSection
            title="Range Default Report Templates"
            templates={defaultReportTemplates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
            isCustomSection={false}
            toReports={toReports}
        />
    )

    const customReportsSection =
        customReportTemplates.length > 0 ? (
            <ReportSection
                title="Custom Report Templates"
                templates={customReportTemplates}
                selectedTemplate={selectedTemplate}
                onTemplateSelect={handleTemplateSelect}
                showManageButton
                isCustomSection={true}
                toReports={toReports}
            />
        ) : (
            <EmptyCustomTemplatesSection toReports={toReports} />
        )

    return (
        <StyledOverlay>
            <StyledContent>
                <FlexColumn css={{ gap: '8px' }}>
                    <Text css={{ fs: '24px', fw: 700, color: '$neutral01' }}>Select Report Template</Text>
                    <Text css={{ fs: '16px', color: '$neutral05' }}>
                        Select the report type preview that you would like to use when generating this report.
                    </Text>
                </FlexColumn>

                {defaultReportsSection}
                {customReportsSection}

                <StyledButtonContainer>
                    <Button variant="secondary" size="lg" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="primary" size="lg" disabled={!selectedTemplate} onClick={handleCreateReport}>
                        Create Report
                    </Button>
                </StyledButtonContainer>
            </StyledContent>
        </StyledOverlay>
    )
}

export default SelectReportTemplateModal
